import { useEffect, useState, type FormEvent } from 'react';
import type { CreateMemberInput, Member } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { MultiSelectSearch } from '../../components/ui/MultiSelectSearch.js';
import { useAgreementSubtypeOptions } from '../agreement-subtypes/api.js';
import { RolePicker } from '../roles/RolePicker.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { useCreateMember, useDeleteMember, useMembers, useUpdateMember } from './api.js';

interface MemberFormModalProps {
  isOpen: boolean;
  member: Member | null;
  onClose: () => void;
}

interface FormState {
  entraOid: string;
  email: string;
  displayName: string;
  isActive: boolean;
  isTimesheetUser: boolean;
  minTimesheetHours: string;
  noTimesheetReminders: boolean;
  isTimeApprover: boolean;
  isProjectManager: boolean;
  isSalesRep: boolean;
  isTechnical: boolean;
  isFunctional: boolean;
  approverIds: string[];
  roleIds: string[];
  serviceCategoryIds: string[];
  agreementSubtypeIds: string[];
  billableTimeBonusThreshold: string;
  percentOfProductRevenue: string;
  activePip: boolean;
}

const EMPTY_FORM: FormState = {
  entraOid: '',
  email: '',
  displayName: '',
  isActive: true,
  isTimesheetUser: false,
  minTimesheetHours: '',
  noTimesheetReminders: false,
  isTimeApprover: false,
  isProjectManager: false,
  isSalesRep: false,
  isTechnical: false,
  isFunctional: false,
  approverIds: [],
  roleIds: [],
  serviceCategoryIds: [],
  agreementSubtypeIds: [],
  billableTimeBonusThreshold: '',
  percentOfProductRevenue: '',
  activePip: false,
};

function toFormState(member: Member | null): FormState {
  if (!member) {
    return EMPTY_FORM;
  }
  return {
    entraOid: member.entraOid,
    email: member.email,
    displayName: member.displayName,
    isActive: member.isActive,
    isTimesheetUser: member.isTimesheetUser,
    minTimesheetHours: member.minTimesheetHours ?? '',
    noTimesheetReminders: member.noTimesheetReminders,
    isTimeApprover: member.isTimeApprover,
    isProjectManager: member.isProjectManager,
    isSalesRep: member.isSalesRep,
    isTechnical: member.isTechnical,
    isFunctional: member.isFunctional,
    approverIds: member.approverIds,
    roleIds: member.roleIds,
    serviceCategoryIds: member.serviceCategoryIds,
    agreementSubtypeIds: member.agreementSubtypeIds,
    billableTimeBonusThreshold: member.billableTimeBonusThreshold ?? '',
    percentOfProductRevenue: member.percentOfProductRevenue ?? '',
    activePip: member.activePip,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function MemberFormModal({
  isOpen,
  member: memberProp,
  onClose,
}: MemberFormModalProps): JSX.Element {
  const [member, setMember] = useState<Member | null>(memberProp);
  const isEdit = member !== null;
  const { isAdmin, principal, impersonator, startImpersonation } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(member));
  const [confirmOpen, setConfirmOpen] = useState(false);

  // An admin (or an admin already impersonating someone) can impersonate any
  // other active member. The real admin is `impersonator.id` while impersonating.
  const canImpersonate = isAdmin || impersonator !== null;
  const realIdentityId = impersonator?.id ?? principal?.id ?? null;
  const showImpersonate =
    isEdit && canImpersonate && member !== null && member.isActive && member.id !== realIdentityId;

  const handleImpersonate = (): void => {
    if (member) {
      startImpersonation(member.id);
      onClose();
    }
  };

  const createMutation = useCreateMember();
  const updateMutation = useUpdateMember();
  const deleteMutation = useDeleteMember();
  const mutation = isEdit ? updateMutation : createMutation;

  const serviceCategoryOptionsQuery = useServiceCategoryOptions();
  const agreementSubtypeOptionsQuery = useAgreementSubtypeOptions();
  const serviceCategoryOptions = (serviceCategoryOptionsQuery.data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
  }));
  const agreementSubtypeOptions = (agreementSubtypeOptionsQuery.data ?? []).map((s) => ({
    id: s.id,
    name: s.name,
  }));

  // Members eligible to be this member's timesheet approver (everyone but self).
  const membersQuery = useMembers({
    page: 1,
    pageSize: 200,
    sortBy: 'displayName',
    sortDir: 'asc',
  });
  const allMembers = (membersQuery.data?.data ?? []).filter((m) => m.id !== member?.id);
  const memberNameById = new Map(allMembers.map((m) => [m.id, m.displayName]));
  const availableApprovers = allMembers.filter((m) => !form.approverIds.includes(m.id));

  const addApprover = (id: string): void => {
    if (!id) return;
    setForm((f) =>
      f.approverIds.includes(id) ? f : { ...f, approverIds: [...f.approverIds, id] },
    );
  };
  const removeApprover = (id: string): void => {
    setForm((f) => ({ ...f, approverIds: f.approverIds.filter((a) => a !== id) }));
  };
  const moveApprover = (index: number, delta: number): void => {
    setForm((f) => {
      const next = [...f.approverIds];
      const target = index + delta;
      if (target < 0 || target >= next.length) return f;
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return { ...f, approverIds: next };
    });
  };

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setMember(memberProp);
    }
  }, [isOpen, memberProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(member));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, member, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save member.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    if (isEdit) {
      updateMutation.mutate(
        {
          id: member.id,
          input: {
            email: form.email.trim(),
            displayName: form.displayName.trim(),
            isActive: form.isActive,
            isTimesheetUser: form.isTimesheetUser,
            minTimesheetHours: form.minTimesheetHours.trim() ? form.minTimesheetHours.trim() : null,
            noTimesheetReminders: form.noTimesheetReminders,
            isTimeApprover: form.isTimeApprover,
            isProjectManager: form.isProjectManager,
            isSalesRep: form.isSalesRep,
            isTechnical: form.isTechnical,
            isFunctional: form.isFunctional,
            approverIds: form.approverIds,
            roleIds: form.roleIds,
            serviceCategoryIds: form.serviceCategoryIds,
            agreementSubtypeIds: form.agreementSubtypeIds,
            billableTimeBonusThreshold: form.billableTimeBonusThreshold.trim()
              ? form.billableTimeBonusThreshold.trim()
              : null,
            percentOfProductRevenue: form.percentOfProductRevenue.trim()
              ? form.percentOfProductRevenue.trim()
              : null,
            activePip: form.activePip,
          },
        },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      const payload: CreateMemberInput = {
        entraOid: form.entraOid.trim(),
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        isActive: form.isActive,
        isTimesheetUser: form.isTimesheetUser,
        minTimesheetHours: form.minTimesheetHours.trim() ? form.minTimesheetHours.trim() : null,
        noTimesheetReminders: form.noTimesheetReminders,
        isTimeApprover: form.isTimeApprover,
        isProjectManager: form.isProjectManager,
        isSalesRep: form.isSalesRep,
        isTechnical: form.isTechnical,
        isFunctional: form.isFunctional,
        approverIds: form.approverIds,
        roleIds: form.roleIds,
        serviceCategoryIds: form.serviceCategoryIds,
        agreementSubtypeIds: form.agreementSubtypeIds,
        billableTimeBonusThreshold: form.billableTimeBonusThreshold.trim()
          ? form.billableTimeBonusThreshold.trim()
          : null,
        percentOfProductRevenue: form.percentOfProductRevenue.trim()
          ? form.percentOfProductRevenue.trim()
          : null,
        activePip: form.activePip,
      };
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setMember(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!member) {
      return;
    }
    deleteMutation.mutate(member.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit member' : 'New member'}
        size="lg"
        footer={
          <FormActions
            formId="member-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
            extra={
              showImpersonate ? (
                <Button
                  variant="secondary"
                  className={isEdit && isAdmin ? '' : 'mr-auto'}
                  onClick={handleImpersonate}
                  disabled={mutation.isPending}
                >
                  Impersonate
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="member-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <TextField
            label="Display name"
            required
            value={form.displayName}
            maxLength={200}
            autoFocus
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            error={fieldErrors.displayName}
          />
          <TextField
            label="Email"
            type="email"
            required
            value={form.email}
            placeholder="user@example.com"
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            error={fieldErrors.email}
          />
          <TextField
            label="Entra object ID"
            required={!isEdit}
            disabled={isEdit}
            value={form.entraOid}
            placeholder="00000000-0000-0000-0000-000000000000"
            onChange={(e) => setForm((f) => ({ ...f, entraOid: e.target.value }))}
            error={fieldErrors.entraOid}
          />
          {isEdit ? (
            <p className="-mt-2 text-xs text-slate-500">
              The Entra object ID is immutable once a member is created.
            </p>
          ) : null}

          <RolePicker
            selectedIds={form.roleIds}
            onChange={(roleIds) => setForm((f) => ({ ...f, roleIds }))}
          />

          <CheckboxField
            label="Timesheet user"
            checked={form.isTimesheetUser}
            onChange={(e) => setForm((f) => ({ ...f, isTimesheetUser: e.target.checked }))}
          />

          <TextField
            label="Min TimeSheet Hours"
            type="number"
            min={0}
            step={0.25}
            value={form.minTimesheetHours}
            placeholder="Blank = no minimum"
            onChange={(e) => setForm((f) => ({ ...f, minTimesheetHours: e.target.value }))}
            error={fieldErrors.minTimesheetHours}
          />
          <p className="-mt-2 text-xs text-slate-500">
            When set, the member&apos;s timesheet must have at least this many hours before it can
            be submitted. Blank allows any total, including zero hours with no entries.
          </p>

          <CheckboxField
            label="No timesheet reminders"
            checked={form.noTimesheetReminders}
            onChange={(e) => setForm((f) => ({ ...f, noTimesheetReminders: e.target.checked }))}
          />

          <CheckboxField
            label="Time Approver"
            checked={form.isTimeApprover}
            onChange={(e) => setForm((f) => ({ ...f, isTimeApprover: e.target.checked }))}
          />

          <CheckboxField
            label="Project Manager"
            checked={form.isProjectManager}
            onChange={(e) => setForm((f) => ({ ...f, isProjectManager: e.target.checked }))}
          />

          <CheckboxField
            label="Sales Rep"
            checked={form.isSalesRep}
            onChange={(e) => setForm((f) => ({ ...f, isSalesRep: e.target.checked }))}
          />

          <CheckboxField
            label="Technical"
            checked={form.isTechnical}
            onChange={(e) => setForm((f) => ({ ...f, isTechnical: e.target.checked }))}
          />

          <CheckboxField
            label="Functional"
            checked={form.isFunctional}
            onChange={(e) => setForm((f) => ({ ...f, isFunctional: e.target.checked }))}
          />

          <MultiSelectSearch
            label="Service categories"
            options={serviceCategoryOptions}
            selected={form.serviceCategoryIds}
            emptyText="No service categories found."
            placeholder="Search service categories…"
            onToggle={(id, checked) =>
              setForm((f) => ({
                ...f,
                serviceCategoryIds: checked
                  ? [...f.serviceCategoryIds, id]
                  : f.serviceCategoryIds.filter((x) => x !== id),
              }))
            }
          />

          <MultiSelectSearch
            label="Agreement subtypes"
            options={agreementSubtypeOptions}
            selected={form.agreementSubtypeIds}
            emptyText="No agreement subtypes found."
            placeholder="Search agreement subtypes…"
            onToggle={(id, checked) =>
              setForm((f) => ({
                ...f,
                agreementSubtypeIds: checked
                  ? [...f.agreementSubtypeIds, id]
                  : f.agreementSubtypeIds.filter((x) => x !== id),
              }))
            }
          />

          <TextField
            label="Billable Time Bonus Threshold"
            type="number"
            value={form.billableTimeBonusThreshold}
            onChange={(e) => setForm((f) => ({ ...f, billableTimeBonusThreshold: e.target.value }))}
            error={fieldErrors.billableTimeBonusThreshold}
          />

          <TextField
            label="Percent of Product Revenue"
            type="number"
            value={form.percentOfProductRevenue}
            onChange={(e) => setForm((f) => ({ ...f, percentOfProductRevenue: e.target.value }))}
            error={fieldErrors.percentOfProductRevenue}
          />

          <CheckboxField
            label="Active PIP"
            checked={form.activePip}
            onChange={(e) => setForm((f) => ({ ...f, activePip: e.target.checked }))}
          />

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-700">Timesheet approvers</span>
            <p className="-mt-1 text-xs text-slate-500">
              Approvers review this member&apos;s submitted timesheets in order. The first approver
              is emailed first; the next is emailed only after the prior approves.
            </p>
            {form.approverIds.length > 0 ? (
              <ol className="flex flex-col gap-1.5">
                {form.approverIds.map((approverId, index) => (
                  <li
                    key={approverId}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
                  >
                    <span className="w-5 shrink-0 text-xs font-medium tabular-nums text-slate-400">
                      {index + 1}.
                    </span>
                    <span className="flex-1 truncate text-sm text-slate-900">
                      {memberNameById.get(approverId) ?? 'Unknown member'}
                    </span>
                    <button
                      type="button"
                      onClick={() => moveApprover(index, -1)}
                      disabled={index === 0}
                      className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveApprover(index, 1)}
                      disabled={index === form.approverIds.length - 1}
                      className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeApprover(approverId)}
                      className="rounded px-1.5 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-slate-400">
                No approvers — submitted timesheets go straight to approved-eligible.
              </p>
            )}
            <SelectField
              label="Add approver"
              value=""
              options={[
                {
                  value: '',
                  label: availableApprovers.length ? 'Select a member…' : 'No more members',
                },
                ...availableApprovers.map((m) => ({ value: m.id, label: m.displayName })),
              ]}
              onChange={(e) => addApprover(e.target.value)}
              error={fieldErrors.approverIds}
            />
          </div>

          <CheckboxField
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete member"
        message={`Delete "${member?.displayName ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
